import { useToast } from '@app/core/notifications';
import { DataExporterFactory, useSearchFilter } from '@mi-front/core';
import { saveAs } from 'file-saver';
import merge from 'lodash/merge';
import omit from 'lodash/omit';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useDidUpdateEffect } from '../../hooks/useDidUpdateEffect';
import appHistory from '../../routing/history';
import { resolveReportFailureMessage } from '../resolve-report-failure-message';
import { ReportType } from '../types';
import { ReportViewProps } from './ReportView';
import { useReportCapture } from './useReportCapture';

function notifyReportFailure(error: unknown, notifyError: (message: string) => void) {
  console.error(error);
  notifyError(resolveReportFailureMessage(error));
}

export type UseReportParams<R extends ReportType> = Pick<
  ReportViewProps<R>,
  | 'defaultValues'
  | 'queryParamsTransform'
  | 'download'
  | 'reportFn'
  | 'getResultCount'
  | 'runOnLoad'
  | 'sort'
  | 'initialResult'
> & {
  skipQuerySyncParams?: string[];
};

/** Sets a form field and submits. Registered by the report form so table actions can update the form. */
export type ReportFormApplyFn = (field: string, value: unknown) => void | Promise<void>;

export type UseReportResult<R extends ReportType> = {
  formats: string[];
  defaultValues?: R['Values'];
  values: R['Values'];
  result: R['Result'] | null;
  resultCount: number | undefined;
  isLoading: boolean;
  setValues: (values: R['Values']) => void;
  setValue: (partialValues: Partial<R['Values']>, runAfter?: boolean) => void;
  runReport: (values: R['Values']) => Promise<void>;
  refreshReport: () => Promise<void>;
  downloadReport: (format: string) => Promise<void>;
  allowDownload: boolean;
  /** Called by the report form to expose set-field + submit to siblings (e.g. the result table). */
  registerFormApply: (apply: ReportFormApplyFn | null) => void;
  /** Applies a field through the form when registered; otherwise updates report values directly. */
  applyFormField: (field: string, value: unknown) => Promise<void>;
};

function getDefaultResultCount(result: unknown): number | undefined {
  const r = result as { total?: number; metadata?: { totalCount?: number } };
  const total = r?.total ?? r?.metadata?.totalCount;
  return typeof total === 'number' ? total : undefined;
}

export function useReport<R extends ReportType>({
  defaultValues,
  queryParamsTransform,
  download,
  reportFn,
  skipQuerySyncParams = [],
  runOnLoad,
  sort,
  getResultCount,
  initialResult,
}: UseReportParams<R>): UseReportResult<R> {
  const { filter, setFilter } = useSearchFilter(appHistory, {
    initialFilter: defaultValues,
    transform: queryParamsTransform,
  });
  const { notifyError } = useToast();
  const notifyReportRun = useReportCapture();

  const [result, setResult] = useState<R['Result'] | null>(initialResult ?? null);
  const [isLoading, setLoading] = useState(false);
  const [values, setValues] = useState<R['Values']>(filter);
  const formApplyRef = useRef<ReportFormApplyFn | null>(null);

  const registerFormApply = useCallback((apply: ReportFormApplyFn | null) => {
    formApplyRef.current = apply;
  }, []);

  useDidUpdateEffect(() => {
    setFilter(omit(values, ...skipQuerySyncParams, 'context'));
  }, [values, sort?.id, sort?.desc]);

  // fetch listing on load
  useEffect(() => {
    if (runOnLoad) {
      runReport(values, true);
    }
  }, []);

  useDidUpdateEffect(() => {
    runReport(values);
  }, [sort?.id, sort?.desc]);

  const runReport = async (values: R['Values'], isLoadRun = false) => {
    if (!isLoadRun) {
      notifyReportRun(values);
    }

    setLoading(true);
    setValues(values);

    try {
      const resultResult = await reportFn(values);
      setResult(resultResult);
    } catch (error) {
      notifyReportFailure(error, notifyError);
    } finally {
      setLoading(false);
    }
  };

  const setValue = (partialValues: Partial<R['Values']>, runAfter = true) => {
    const nextValues = merge({}, values, partialValues);

    if (runAfter) {
      runReport(nextValues);
    } else {
      setValues(nextValues);
    }
  };

  const applyFormField = async (field: string, value: unknown) => {
    if (formApplyRef.current) {
      await formApplyRef.current(field, value);
      return;
    }

    await runReport({
      ...values,
      [field]: value,
    });
  };

  const refreshReport = async () => {
    runReport(values);
  };

  const downloadReport = async (format: string) => {
    if (!download) {
      return;
    }

    try {
      if (download.remote) {
        const data = await download.remote(values, format);
        saveAs(new Blob([data as BlobPart]), `${download.filename}.${format}`);
        return;
      }

      const filenameForData = (data: R['Result'] | null): string => {
        return typeof download.filename === 'string' ? download.filename : download.filename(data!);
      };

      if (download.local) {
        const data = result ? result : await reportFn(values);

        const dataToExport = Array.isArray(data)
          ? data
          : download.resultToExport
            ? download.resultToExport(data)
            : [];

        DataExporterFactory.get(format).exportJson(filenameForData(data), dataToExport as object[]);
      } else {
        const data = await reportFn(values, format);

        if (!data) {
          return;
        }

        // If data is a URL, navigate to it
        if (
          typeof data === 'string' &&
          (data.startsWith('http://') || data.startsWith('https://'))
        ) {
          window.location.href = data;
          return;
        }

        const filename = filenameForData(data);

        if (format === 'xls') {
          const blob = new Blob([data as BlobPart], {
            type: 'application/vnd.ms-excel',
          });
          saveAs(blob, `${filename}.xls`);
        } else if (format === 'csv') {
          const blob = new Blob([data as BlobPart], { type: 'text/csv' });
          saveAs(blob, `${filename}.csv`);
        }
      }
    } catch (error) {
      notifyReportFailure(error, notifyError);
    }
  };

  const resultCount =
    result !== null
      ? getResultCount
        ? getResultCount(result)
        : getDefaultResultCount(result)
      : undefined;

  return {
    defaultValues,
    values,
    setValues,
    setValue,
    result,
    resultCount,
    isLoading: isLoading,
    runReport,
    downloadReport,
    allowDownload: !!download,
    formats: ['csv', 'xls'],
    refreshReport,
    registerFormApply,
    applyFormField,
  };
}
